<template>
  <v-row>
    <v-col xl="4" lg="4" md="4" sm="12" xs="12">
      <v-table class="hide_scroll compactTable padding-xs">
        <thead>
          <tr>
            <th id="th_fieldComparison" aria-label="blankTableHeader" style="width: 55%" class="align-center" />
            <th id="th_current" class="align-center">Current</th>
            <th id="th_previous" class="align-center">Previous</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="field in fieldsToCompare" :key="field.rowName">
            <td
              class="boldFont"
              :class="{
                difference: field.differing || field.nonControllingChange,
              }"
            >
              {{ field.rowName }}: {{ field.differing ? "*" : "" }}
            </td>
            <td
              class="align-center"
              :class="{
                difference: field.differing || field.nonControllingChange,
              }"
            >
              <span :class="{ boldFont: field.nonControllingChange }"
                >{{ field.currentCol }}{{ field.nonControllingChange ? " *" : "" }}</span
              >
            </td>
            <td
              class="align-center"
              :class="{
                difference: field.differing || field.nonControllingChange,
              }"
            >
              {{ field.previousCol }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-col>
    <v-col xl="8" lg="8" md="8" sm="12" xs="12">
      <v-row>
        <v-col align="center" xl="2" lg="4" md="4" sm="4" xs="4">
          <span>&nbsp;</span>
          <v-table class="noBorders hide_scroll compactTable pt-2 padding-xs">
            <thead>
              <tr>
                <th id="th_empty" aria-label="blankTableHeader">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              <tr class="tableRowHeight">
                <td class="rightAlign pt-2 pb-1">2- Routine</td>
              </tr>
              <tr class="tableRowHeight">
                <td class="rightAlign pt-2 pb-1">3 - Underwater</td>
              </tr>
              <tr class="tableRowHeight">
                <td class="rightAlign pt-2 pb-1">4 - NSTM</td>
              </tr>
              <tr class="tableRowHeight">
                <td class="rightAlign pt-2 pb-1">6 - In-Depth</td>
              </tr>
              <tr class="tableRowHeight">
                <td class="rightAlign pt-2 pb-1">7 - Special</td>
              </tr>
              <tr class="tableRowHeight">
                <td class="rightAlign pt-2 pb-1">8 - Service</td>
              </tr>
              <tr class="tableRowHeight">
                <td class="rightAlign pt-2 pb-1">E - Elements</td>
              </tr>
            </tbody>
          </v-table>
        </v-col>
        <v-col xl="3" lg="3" md="3" sm="3" xs="3" align="center">
          <v-label class="bold">IE.01 Required</v-label>
          <v-table class="hide_scroll compactTable padding-xs">
            <thead>
              <tr>
                <th id="th_current" class="align-center">Current</th>
                <th id="th_previous" class="align-center">Previous</th>
              </tr>
            </thead>
            <tbody>
              <tr class="tableRowHeight align-center">
                <td>N/A</td>
                <td>N/A</td>
              </tr>
              <tr
                class="tableRowHeight align-center"
                v-for="requiredInsp in requiredInspToCompare"
                :key="requiredInsp.rowName"
              >
                <td :class="{ difference: requiredInsp.differing }">
                  <v-checkbox
                    class="d-flex justify-center"
                    id="{{ requiredInsp.currentId }}"
                    v-model="requiredInsp.currentModel"
                    true-value="1"
                    false-value="0"
                    disabled
                  />
                </td>
                <td :class="{ difference: requiredInsp.differing }">
                  <v-checkbox
                    class="d-flex justify-center"
                    id="{{ requiredInsp.previousId }}"
                    :model-value="requiredInsp.previousModel"
                    true-value="1"
                    false-value="0"
                    disabled
                  />
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>
        <v-col xl="3" lg="3" md="3" sm="3" xs="3" align="center">
          <v-label class="bold">IE.03 Interval</v-label>
          <v-table class="hide_scroll compactTable padding-xs">
            <thead>
              <tr>
                <th id="th_intervalCurrent" class="align-center">Current</th>
                <th id="th_intervalPrevious" class="align-center">Previous</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="interval in intervalsToCompare" :key="interval.rowName" class="tableRowHeight align-center">
                <td :class="{ difference: interval.differing }">
                  {{ interval.currentInterval }}
                </td>
                <td :class="{ difference: interval.differing }">
                  {{ interval.previousInterval }}
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
<script setup>
import { getFormattedDateStringNoTime } from "@/composables/dateField.js";
import { useInspectionStore } from "@/stores/inspection";
import { computed, ref, onMounted, watch } from "vue";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { useQuickSearchStore } from "@/stores/quickSearch";
import { storeToRefs } from "pinia";
import { useKeyfieldsStore } from "@/components/inspection/store/keyfields";

const props = defineProps({
  showPerformedData: { default: false, type: Boolean },
  moduleType: { default: MODULE_TYPES.INSPECTION },
});

const inspectionStore = useInspectionStore();
const structure = computed(() => inspectionStore.selectedInspection);
const overallScourHoles = computed({
  get() {
    return keyFieldsValue?.value?.currentInsp?.OVERALL_SCOUR_HOLE;
  },
});
const prevOverallScourHoles = computed({
  get() {
    return keyFieldsValue?.value?.previousInsp?.OVERALL_SCOUR_HOLE;
  },
});
const quickSearchStore = useQuickSearchStore();
const structureHeader = computed(() => quickSearchStore.getStructure());
let keyFieldsValue = ref({});
const { hasQuickSearchSucceeded } = storeToRefs(quickSearchStore);
const keyfieldsStore = useKeyfieldsStore();

onMounted(async () => {
  await readData();
});

const readData = async () => {
  keyFieldsValue.value = {};
  if (props.moduleType == MODULE_TYPES.MANAGEMENT && structureHeader.value?.BRKEY) {
    if (!hasQuickSearchSucceeded.value) {
      return;
    }
    await keyfieldsStore.loadDataObject(
      props.moduleType,
      structureHeader.value.BRKEY,
      structureHeader.value.INSPECTIONID,
    );
    keyFieldsValue.value = keyfieldsStore.getKeyfieldsObject();
  } else if (props.moduleType == MODULE_TYPES.INSPECTION) {
    await keyfieldsStore.loadDataObject(props.moduleType, null, null, structure.value);
    keyFieldsValue.value = keyfieldsStore.getKeyfieldsObject();
  }
  refreshKeyFieldsArrays();
};

let fieldsToCompare = ref({});
const setFieldsToCompare = () => {
  fieldsToCompare.value = [
    {
      rowName: "IE.05 Inspection Date",
      currentCol: getFormattedDateStringNoTime(keyFieldsValue?.value?.currentInsp?.INSPDATE),
      previousCol: getFormattedDateStringNoTime(keyFieldsValue?.value?.previousInsp?.INSPDATE),
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "IE.08 Inspection Type",
      currentCol: keyFieldsValue?.value?.currentInsp?.INSPTYPE || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.INSPTYPE || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.01 Deck",
      currentCol: keyFieldsValue?.value?.currentInsp?.DKRATING || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.DKRATING || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.02 Superstructure",
      currentCol: keyFieldsValue?.value?.currentInsp?.SUPRATING || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.SUPRATING || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.03 Substructure",
      currentCol: keyFieldsValue?.value?.currentInsp?.SUBRATING || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.SUBRATING || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.04 Culvert",
      currentCol: keyFieldsValue?.value?.currentInsp?.CULVRATING || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.CULVRATING || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.05 Railings",
      currentCol: keyFieldsValue?.value?.currentInsp?.RAILRATING || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.RAILRATING || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.06 Transition",
      currentCol: keyFieldsValue?.value?.currentInsp?.TRANSRATIN || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.TRANSRATIN || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.07 Bearings",
      currentCol: keyFieldsValue?.value?.currentInsp?.BEARING_OVERALL_COND_RATE || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.BEARING_OVERALL_COND_RATE || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.08 Joints",
      currentCol: keyFieldsValue?.value?.currentInsp?.JOINTS_OVERALL_COND_RATE || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.JOINTS_OVERALL_COND_RATE || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.09 Channel",
      currentCol: keyFieldsValue?.value?.currentInsp?.CHANRATING || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.CHANRATING || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.10 Channel Protection",
      currentCol: keyFieldsValue?.value?.currentInsp?.CHAN_PROT_RATING || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.CHAN_PROT_RATING || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.11 Scour",
      currentCol: overallScourHoles.value && overallScourHoles.value != "" ? overallScourHoles.value : "--",
      previousCol:
        prevOverallScourHoles.value && prevOverallScourHoles.value != "" ? prevOverallScourHoles.value : "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.14 NSTM",
      currentCol: keyFieldsValue?.value?.currentInsp?.NSTM_CONDITION || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.NSTM_CONDITION || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "B.C.15 Underwater",
      currentCol: keyFieldsValue?.value?.currentInsp?.UNDERWATER_RATING || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.UNDERWATER_RATING || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "WA.01 SCBI",
      currentCol: keyFieldsValue?.value?.currentInsp?.SCOURCRIT || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.SCOURCRIT || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "WA.02 SCBI Category",
      currentCol: keyFieldsValue?.value?.currentInsp?.SCOUR_CRIT_CTGRY || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.SCOUR_CRIT_CTGRY || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      rowName: "WA.43 Streambed Material",
      currentCol: keyFieldsValue?.value?.currentInsp?.STREAM_BED_MATERIAL || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.STREAM_BED_MATERIAL || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
    {
      nonControllingChange: keyFieldsValue?.value?.currentInsp?.STREAM_BED_MATERIAL_NON_CONTROLLING_CHANGE,
    },
    {
      rowName: "B.C.12 Bridge Condition",
      currentCol: keyFieldsValue?.value?.currentInsp?.CONDITION || "--",
      previousCol: keyFieldsValue?.value?.previousInsp?.CONDITION || "--",
      get differing() {
        return this.currentCol != this.previousCol;
      },
    },
  ];
};

let requiredInspToCompare = ref({});
const setRequiredInspToCompare = () => {
  requiredInspToCompare.value = [];
  requiredInspToCompare.value = [
    {
      currentId: "checkbox_uw_requiredCurrent",
      currentModel: keyFieldsValue?.value?.currentInsp?.UWREQUIRED ?? false,
      previousId: "checkbox_uw_requiredPrevious",
      previousModel: keyFieldsValue?.value?.previousInsp?.UWREQUIRED ?? false,
      get differing() {
        return this.currentModel != this.previousModel;
      },
    },
    {
      currentId: "checkbox_nstm_requiredCurrent",
      currentModel: keyFieldsValue?.value?.currentInsp?.NSTMREQUIRED ?? false,
      previousId: "checkbox_nstm_requiredPrevious",
      previousModel: keyFieldsValue?.value?.previousInsp?.NSTMREQUIRED ?? false,
      get differing() {
        return this.currentModel != this.previousModel;
      },
    },
    {
      currentId: "checkbox_ind_requiredCurrent",
      currentModel: keyFieldsValue?.value?.currentInsp?.INDREQUIRED ?? false,
      previousId: "checkbox_ind_requiredPrevious",
      previousModel: keyFieldsValue?.value?.previousInsp?.INDREQUIRED ?? false,
      get differing() {
        return this.currentModel != this.previousModel;
      },
    },
    {
      currentId: "checkbox_os_requiredCurrent",
      currentModel: keyFieldsValue?.value?.currentInsp?.OSREQUIRED ?? false,
      previousId: "checkbox_os_requiredPrevious",
      previousModel: keyFieldsValue?.value?.previousInsp?.OSREQUIRED ?? false,
      get differing() {
        return this.currentModel != this.previousModel;
      },
    },
    {
      currentId: "checkbox_serv_requiredCurrent",
      currentModel: keyFieldsValue?.value?.currentInsp?.SERVREQUIRED ?? false,
      previousId: "checkbox_serv_requiredPrevious",
      previousModel: keyFieldsValue?.value?.previousInsp?.SERVREQUIRED ?? false,
      get differing() {
        return this.currentModel != this.previousModel;
      },
    },
    {
      currentId: "checkbox_elem_requiredCurrent",
      currentModel: keyFieldsValue?.value?.currentInsp?.ELEMREQUIRED ?? false,
      previousId: "checkbox_elem_requiredPrevious",
      previousModel: keyFieldsValue?.value?.previousInsp?.ELEMREQUIRED ?? false,
      get differing() {
        return this.currentModel != this.previousModel;
      },
    },
  ];
};

let intervalsToCompare = ref({});
const setIntervalsToCompare = () => {
  intervalsToCompare.value = [];
  intervalsToCompare.value = [
    {
      currentInterval: keyFieldsValue?.value?.currentInsp?.ROUINTERVAL
        ? keyFieldsValue?.value?.currentInsp?.ROUINTERVAL + " mos"
        : "--",
      previousInterval: keyFieldsValue?.value?.previousInsp?.ROUINTERVAL
        ? keyFieldsValue?.value?.previousInsp?.ROUINTERVAL + " mos"
        : "--",
      get differing() {
        return this.currentInterval != this.previousInterval;
      },
    },
    {
      currentInterval: keyFieldsValue?.value?.currentInsp?.UWINTERVAL
        ? keyFieldsValue?.value?.currentInsp?.UWINTERVAL + " mos"
        : "--",
      previousInterval: keyFieldsValue?.value?.previousInsp?.UWINTERVAL
        ? keyFieldsValue?.value?.previousInsp?.UWINTERVAL + " mos"
        : "--",
      get differing() {
        return this.currentInterval != this.previousInterval;
      },
    },
    {
      currentInterval: keyFieldsValue?.value?.currentInsp?.NSTMINTERVAL
        ? keyFieldsValue?.value?.currentInsp?.NSTMINTERVAL + " mos"
        : "--",
      previousInterval: keyFieldsValue?.value?.previousInsp?.NSTMINTERVAL
        ? keyFieldsValue?.value?.previousInsp?.NSTMINTERVAL + " mos"
        : "--",
      get differing() {
        return this.currentInterval != this.previousInterval;
      },
    },
    {
      currentInterval: keyFieldsValue?.value?.currentInsp?.INDINTERVAL
        ? keyFieldsValue?.value?.currentInsp?.INDINTERVAL + " mos"
        : "--",
      previousInterval: keyFieldsValue?.value?.previousInsp?.INDINTERVAL
        ? keyFieldsValue?.value?.previousInsp?.INDINTERVAL + " mos"
        : "--",
      get differing() {
        return this.currentInterval != this.previousInterval;
      },
    },
    {
      currentInterval: keyFieldsValue?.value?.currentInsp?.OSINTERVAL
        ? keyFieldsValue?.value?.currentInsp?.OSINTERVAL + " mos"
        : "--",
      previousInterval: keyFieldsValue?.value?.previousInsp?.OSINTERVAL
        ? keyFieldsValue?.value?.previousInsp?.OSINTERVAL + " mos"
        : "--",
      get differing() {
        return this.currentInterval != this.previousInterval;
      },
    },
    {
      currentInterval: keyFieldsValue?.value?.currentInsp?.SERVINTERVAL
        ? keyFieldsValue?.value?.currentInsp?.SERVINTERVAL + " mos"
        : "--",
      previousInterval: keyFieldsValue?.value?.previousInsp?.SERVINTERVAL
        ? keyFieldsValue?.value?.previousInsp?.SERVINTERVAL + " mos"
        : "--",
      get differing() {
        return this.currentInterval != this.previousInterval;
      },
    },
    {
      currentInterval: keyFieldsValue?.value?.currentInsp?.ELEMINTERVAL
        ? keyFieldsValue?.value?.currentInsp?.ELEMINTERVAL + " mos"
        : "--",
      previousInterval: keyFieldsValue?.value?.previousInsp?.ELEMINTERVAL
        ? keyFieldsValue?.value?.previousInsp?.ELEMINTERVAL + " mos"
        : "--",
      get differing() {
        return this.currentInterval != this.previousInterval;
      },
    },
  ];
};

const refreshKeyFieldsArrays = () => {
  setFieldsToCompare();
  setRequiredInspToCompare();
  setIntervalsToCompare();
};

watch(
  () => [props],
  () => {
    readData();
  },
  { deep: true },
);

watch(
  () => [keyFieldsValue],
  () => {
    refreshKeyFieldsArrays();
  },
  { deep: true },
);

defineExpose({
  readData,
});
</script>
<style scoped lang="scss">
@use "@/styles/general" as g;
@use "@/styles/colors" as c;

.boldFont {
  font-weight: bold !important;
}

.tableRowHeight {
  height: 40px;
}

.rightAlign {
  text-align: right;
  font-weight: bold !important;
}

.difference {
  background-color: c.$e-yellow;
}
</style>
